/**
 * Hooks for collection request management (Velociraptor workflow)
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  CollectionRequest,
  CreateCollectionRequestInput,
  UpdateCollectionRequestInput,
  UpdatePackageInfoRequest,
  MarkSentRequest,
  MarkIngestedRequest,
  StatusCountResponse,
  ListCollectionRequestsQuery,
} from '../types/case-container'
import {
  listCollectionRequests,
  getStatusCounts,
  getCollectionRequest,
  createCollectionRequest,
  updateCollectionRequest,
  startGeneration,
  updatePackageInfo,
  markSent,
  markIngested,
  overrideRequestStatus,
  cancelRequest,
  deleteCollectionRequest,
  getMarkdownDocument,
  getCollectionForm,
  downloadDocument,
} from '../services/collection-requests'
import type {
  DocumentResponse,
  OverrideRequestStatusInput,
  StallRequestInput,
} from '../types/collection-requests'
import { runMutation } from './use-collection-requests/run-mutation'
import { invalidatingThen } from './use-collection-requests/invalidating-then'
import { useStallMutations } from './use-collection-requests/use-stall-mutations'
import { useInvalidate, useInvalidationSubscription } from './use-list-invalidation'

// =============================================================================
// useCollectionRequests - List collection requests for a case
// =============================================================================

const EMPTY_STATUS_COUNTS: StatusCountResponse = { packageStatus: [], returnStatus: [] }

interface UseCollectionRequestsResult {
  requests: CollectionRequest[]
  statusCounts: StatusCountResponse
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch collection requests for a case
 */
export function useCollectionRequests(
  caseId: string | null,
  params?: ListCollectionRequestsQuery
): UseCollectionRequestsResult {
  const [requests, setRequests] = useState<CollectionRequest[]>([])
  const [statusCounts, setStatusCounts] = useState<StatusCountResponse>(EMPTY_STATUS_COUNTS)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const reqPackageStatus = params?.packageStatus
  const reqReturnStatus = params?.returnStatus
  const reqIsExternal = params?.isExternal
  const collectorType = params?.collectorType

  const fetch = useCallback(async () => {
    if (!caseId) {
      setRequests([])
      setStatusCounts(EMPTY_STATUS_COUNTS)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const [requestsData, countsData] = await Promise.all([
        listCollectionRequests(caseId, params),
        getStatusCounts(caseId),
      ])
      setRequests(requestsData)
      setStatusCounts(countsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collection requests')
      setRequests([])
      setStatusCounts(EMPTY_STATUS_COUNTS)
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, reqPackageStatus, reqReturnStatus, reqIsExternal, collectorType])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useInvalidationSubscription('collection_requests', fetch)

  return { requests, statusCounts, loading, error, refetch: fetch }
}

// =============================================================================
// useCollectionRequest - Single collection request
// =============================================================================

interface UseCollectionRequestResult {
  request: CollectionRequest | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch a single collection request
 */
export function useCollectionRequest(
  caseId: string | null,
  requestId: string | null
): UseCollectionRequestResult {
  const [request, setRequest] = useState<CollectionRequest | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !requestId) {
      setRequest(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getCollectionRequest(caseId, requestId)
      setRequest(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collection request')
      setRequest(null)
    } finally {
      setLoading(false)
    }
  }, [caseId, requestId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { request, loading, error, refetch: fetch }
}

// =============================================================================
// useCollectionRequestMutations - Mutations for collection requests
// =============================================================================

interface UseCollectionRequestMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  create: (caseId: string, input: CreateCollectionRequestInput) => Promise<CollectionRequest | null>
  update: (
    caseId: string,
    requestId: string,
    input: UpdateCollectionRequestInput
  ) => Promise<CollectionRequest | null>
  generate: (caseId: string, requestId: string) => Promise<CollectionRequest | null>
  setPackageInfo: (
    caseId: string,
    requestId: string,
    info: UpdatePackageInfoRequest
  ) => Promise<CollectionRequest | null>
  send: (
    caseId: string,
    requestId: string,
    info: MarkSentRequest
  ) => Promise<CollectionRequest | null>
  ingest: (
    caseId: string,
    requestId: string,
    info: MarkIngestedRequest
  ) => Promise<CollectionRequest | null>
  cancel: (caseId: string, requestId: string) => Promise<CollectionRequest | null>
  overrideStatus: (
    caseId: string,
    requestId: string,
    input: OverrideRequestStatusInput
  ) => Promise<CollectionRequest | null>
  stall: (
    caseId: string,
    requestId: string,
    input: StallRequestInput
  ) => Promise<CollectionRequest | null>
  unstall: (caseId: string, requestId: string) => Promise<CollectionRequest | null>
  remove: (caseId: string, requestId: string) => Promise<boolean>
  clearError: () => void
}

/**
 * Hook for collection request mutations
 */
export function useCollectionRequestMutations(): UseCollectionRequestMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invalidate = useInvalidate()

  const create = useCallback(
    (caseId: string, input: CreateCollectionRequestInput) => runMutation<CollectionRequest | null>(
      { setLoading: setCreating, setError, errorMessage: 'Failed to create collection request', fallback: null },
      () => createCollectionRequest(caseId, input)
    ), []
  )

  const update = useCallback(
    (caseId: string, requestId: string, input: UpdateCollectionRequestInput) => runMutation<CollectionRequest | null>(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to update collection request', fallback: null },
      () => updateCollectionRequest(caseId, requestId, input)
    ), []
  )

  const generate = useCallback(
    (caseId: string, requestId: string) => runMutation<CollectionRequest | null>(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to start package generation', fallback: null },
      () => startGeneration(caseId, requestId)
    ), []
  )

  const setPackageInfo = useCallback(
    (caseId: string, requestId: string, info: UpdatePackageInfoRequest) => runMutation<CollectionRequest | null>(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to update package info', fallback: null },
      () => updatePackageInfo(caseId, requestId, info)
    ), []
  )

  const send = useCallback(
    (caseId: string, requestId: string, info: MarkSentRequest) => runMutation<CollectionRequest | null>(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to mark as sent', fallback: null },
      () => invalidatingThen(markSent(caseId, requestId, info), invalidate, ['collection_requests', 'case_endpoints'])
    ), [invalidate]
  )

  const ingest = useCallback(
    (caseId: string, requestId: string, info: MarkIngestedRequest) => runMutation<CollectionRequest | null>(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to mark as ingested', fallback: null },
      () => invalidatingThen(markIngested(caseId, requestId, info), invalidate, ['collection_requests', 'collection_receipts', 'case_endpoints'])
    ), [invalidate]
  )

  const cancel = useCallback(
    (caseId: string, requestId: string) => runMutation<CollectionRequest | null>(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to cancel request', fallback: null },
      () => cancelRequest(caseId, requestId)
    ), []
  )

  const overrideStatus = useCallback(
    (caseId: string, requestId: string, input: OverrideRequestStatusInput) => runMutation<CollectionRequest | null>(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to override request status', fallback: null },
      () => invalidatingThen(overrideRequestStatus(caseId, requestId, input), invalidate, ['collection_requests', 'case_activity'])
    ), [invalidate]
  )

  const { stall, unstall } = useStallMutations({ setUpdating, setError, invalidate })

  const remove = useCallback(
    (caseId: string, requestId: string) => runMutation<boolean>(
      { setLoading: setDeleting, setError, errorMessage: 'Failed to delete collection request', fallback: false },
      async () => { await deleteCollectionRequest(caseId, requestId); return true }
    ), []
  )

  const clearError = useCallback(() => { setError(null) }, [])

  return {
    creating, updating, deleting, error,
    create, update, generate, setPackageInfo,
    send, ingest, cancel, overrideStatus, stall, unstall, remove, clearError,
  }
}

// =============================================================================
// useCollectionDocuments - Document generation for collection requests
// =============================================================================

interface UseCollectionDocumentsResult {
  loading: boolean
  error: string | null
  getMarkdown: (caseId: string, requestId: string) => Promise<DocumentResponse | null>
  getForm: (caseId: string, requestId: string) => Promise<DocumentResponse | null>
  downloadMarkdown: (caseId: string, requestId: string) => Promise<boolean>
  downloadForm: (caseId: string, requestId: string) => Promise<boolean>
}

/**
 * Hook for generating and downloading collection documents
 */
export function useCollectionDocuments(): UseCollectionDocumentsResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getMarkdown = useCallback(
    (caseId: string, requestId: string): Promise<DocumentResponse | null> =>
      runMutation<DocumentResponse | null>(
        {
          setLoading,
          setError,
          errorMessage: 'Failed to generate markdown document',
          fallback: null,
        },
        () => getMarkdownDocument(caseId, requestId)
      ),
    []
  )

  const getForm = useCallback(
    (caseId: string, requestId: string): Promise<DocumentResponse | null> =>
      runMutation<DocumentResponse | null>(
        {
          setLoading,
          setError,
          errorMessage: 'Failed to generate collection form',
          fallback: null,
        },
        () => getCollectionForm(caseId, requestId)
      ),
    []
  )

  const downloadMarkdown = useCallback(
    (caseId: string, requestId: string): Promise<boolean> =>
      runMutation<boolean>(
        {
          setLoading,
          setError,
          errorMessage: 'Failed to download markdown document',
          fallback: false,
        },
        async () => {
          const doc = await getMarkdownDocument(caseId, requestId)
          downloadDocument(doc)
          return true
        }
      ),
    []
  )

  const downloadForm = useCallback(
    (caseId: string, requestId: string): Promise<boolean> =>
      runMutation<boolean>(
        {
          setLoading,
          setError,
          errorMessage: 'Failed to download collection form',
          fallback: false,
        },
        async () => {
          const doc = await getCollectionForm(caseId, requestId)
          downloadDocument(doc)
          return true
        }
      ),
    []
  )

  return { loading, error, getMarkdown, getForm, downloadMarkdown, downloadForm }
}
